import { useGetAllRoadmapItemsQuery } from '../../modules/roadmap/api'
import styled from 'styled-components'
import { Box } from '../../shared_components/UI/Box'
import AnimatedLoader from '../../shared_components/AnimatedLoader'
import { Card, Divider } from '../../shared_components/Library/Card'
import { Chip } from '../../shared_components/Library/Chip'
import { avatarReference } from '../../styles/staff-avatars'
import {
  H1,
  H3,
  H4,
  H5,
  Span,
} from '../../shared_components/Library/Typeography'
import Hero from '../../shared_components/Library/Hero'

import { indexGutterMargin, useShouldShowForThreshold } from '../../theme'

import { OverflowingCarousel } from '../../shared_components/Library/OverflowingCarousel'

import Dotdotdot from 'react-dotdotdot'

export interface IRoadmapTask {
  id: number
  title: string
  description: string
  status: RoadmapTaskStatus
  department: RoadmapTaskDepartment[]
  releaseDate: string
  assignedByName: string
}

export enum RoadmapTaskStatus {
  MAYBE_LATER = 'Maybe later',
  BACKLOG = 'Backlog',
  NEXT_UP = 'Next up',
  IN_PROGRESS = 'In progress',
  DONE = 'Done',
}

export enum RoadmapTaskDepartment {
  ENGINEERING = 'Engineering',
  DESIGN = 'Design',
  PRODUCT = 'Product',
  MARKETING = 'Marketing',
  SALES = 'Sales',
  CUSTOMER_SUCCESS = 'Customer Success',
  HR = 'HR',
  FINANCE = 'Finance',
}

const DESIRED_STATUSES = ['NEXT_UP', 'IN_PROGRESS', 'DONE']

const Roadmap = () => {
  const { data: roadmapTasksData, isLoading } = useGetAllRoadmapItemsQuery(
    {},
    { refetchOnMountOrArgChange: true },
  )
  const roadmapTasks: IRoadmapTask[] = roadmapTasksData?.roadmapTasks ?? []

  const isAtLeastMediumDisplaySize = useShouldShowForThreshold('medium')

  return (
    <Box dc padB="section">
      <Hero
        Title={HeroTitle}
        Subtitle={HeroSubtitle}
        CallsToAction={HeroCTA}
        imageUrl="/roadmap-banner.png"
        hasFullHeightImage
        hasNoLeftPadding
      />
      <Outer wrap="wrap">
        {isLoading ? (
          <Box>
            <AnimatedLoader size={32} onlyWhite />
          </Box>
        ) : (
          DESIRED_STATUSES.map((status, i) => {
            const filteredRoadmapTasks = roadmapTasks.filter(
              (roadmapTask) => roadmapTask.status === status,
            )
            const statusKey = status as keyof typeof RoadmapTaskDepartment
            const tasksCount = filteredRoadmapTasks.length
            const isLast = i === DESIRED_STATUSES.length - 1
            return (
              <>
                <Column g={16} dc key={status}>
                  <Box ac g={8} className="status-title" jsb fu>
                    <Box ac g={8}>
                      <img
                        src={`/img/icons/${toKebabCase(statusKey)}.svg`}
                        className="status-icon"
                        alt={`${statusKey} icon`}
                      />
                      <H3 s="L" fw="semibold">
                        {RoadmapTaskStatus[statusKey]}
                      </H3>
                    </Box>
                    <Box aw fu>
                      <H5 s="L" c="text-brandPrimary">
                        {tasksCount}
                      </H5>
                    </Box>
                  </Box>
                  <CardContainer>
                    <>
                      {filteredRoadmapTasks.map((roadmapTask) => (
                        <RoadmapItemCard
                          roadmapTask={roadmapTask}
                          key={`${status}-${roadmapTask.id}`}
                        />
                      ))}
                    </>
                  </CardContainer>
                </Column>
                {!isLast && isAtLeastMediumDisplaySize && <Separator igmh />}
                {!isLast && !isAtLeastMediumDisplaySize && (
                  <Box padV={16}>
                    <Divider />
                  </Box>
                )}
              </>
            )
          })
        )}
      </Outer>
    </Box>
  )
}

function CardContainer({ children }) {
  const isAtLeastMediumDisplaySize = useShouldShowForThreshold('medium')
  if (isAtLeastMediumDisplaySize) {
    return (
      <Box dc g={16}>
        {children}
      </Box>
    )
  }
  return <OverflowingCarousel indexGutters>{children}</OverflowingCarousel>
}

const Outer = styled(Box)`
  min-height: max-content;
  align-items: stretch;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.medium}px) {
    flex-direction: column;
  }
`

const Column = styled(Box)``

const Separator = styled.div`
  min-width: 1px;
  background: var(--stroke-secondary);
  ${indexGutterMargin}
`

function HeroTitle() {
  return <H1 uc>Roadmap</H1>
}

function HeroSubtitle() {
  return (
    <Box dc padV="l">
      <Span s="XL">
        A high level view at what ZAP are cooking. This roadmap only includes
        major releases, for a more detailed view, see our changelog
      </Span>
    </Box>
  )
}

function HeroCTA() {
  return <></>
}

export default Roadmap

function RoadmapItemCard({ roadmapTask }) {
  const { title, description, department, releaseDate, assignedByName, link } =
    roadmapTask
  const avatarUrl = avatarReference[assignedByName]
  const isAtLeastMediumDisplaySize = useShouldShowForThreshold('medium')

  return (
    <Card
      mw={isAtLeastMediumDisplaySize ? 'unset' : '332px'}
      minW={isAtLeastMediumDisplaySize ? 'unset' : '332px'}
      mh="232px"
    >
      <Box dc asb g={8}>
        <Box dc>
          <Box ac jsb g={8} padB={8}>
            <H4 s="S" fw="semibold">
              {title}
            </H4>
            {link && (
              <a href={link} target="_blank" rel="noreferrer noopener">
                <img
                  src="/img/icons/roadmap-chevron.svg"
                  alt="External link"
                  className="external-link"
                />
              </a>
            )}
          </Box>
          <Box padV={4}>
            <Dotdotdot clamp="66px">
              <Span s="L" c="text-secondary">
                {description}
              </Span>
            </Dotdotdot>
          </Box>
          <Box ac g={4} padV={8}>
            {department.map((dep: string, i: number) => (
              <Chip key={i}>
                <Box padH={4}>
                  <Span s="S">{toTitleCase(dep.toLowerCase())}</Span>
                </Box>
              </Chip>
            ))}
          </Box>
        </Box>
        <Box dc g={8}>
          <Box ac jsb>
            <span className="label">Release</span>
            <strong>{releaseDate}</strong>
          </Box>
          <Divider />
          <Box ac jsb>
            <span className="label">Assignee</span>
            <strong>
              <Box ac g={4}>
                {toTitleCase(assignedByName)}
                <Box mw="20px">
                  <img src={avatarUrl} alt="Avatar" className="avatar" />
                </Box>
              </Box>
            </strong>
          </Box>
        </Box>
      </Box>
    </Card>
  )
}

function toKebabCase(str: string) {
  return str.replaceAll('_', '-').toLowerCase()
}

function toTitleCase(str: string) {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

type PsuedoDate = string
function psuedoDateToRealDate(date: PsuedoDate) { }
